'use client';

/**
 * Fires select_item event for Google Analytics. *Note: Do not ever push PII into the data layer!!*
 *
 * @example
 * const { fireEvent } = useSelectItemEvent();
 * fireEvent(items);
 */

import { useCallback } from 'react';
import { sendGTMEvent } from '@next/third-parties/google';
import { EVENT_SELECT_ITEM } from 'constants/analyticsConstants';
import { OrderItemPrice } from 'services/cart/types';
import { OrderItemPart } from 'services/cart';
import { getAttributeValueForCart } from 'utils/gaEventAnalytics';
import { CartItem } from 'hooks/analytics/types';
import {
  SearchItemSummaryData,
  SearchSelectItemData
} from 'app/[lang]/[esite]/(core)/(search-display)/_components/analytics-listener/types';

type AddToCartType = {
  item: OrderItemPart | SearchSelectItemData;
  priceData?: OrderItemPrice;
  partName: string;
  locationId: string;
  itemSummary: Product | SearchItemSummaryData;
  isSearchPageEvent?: boolean;
  itemListName?: string;
  itemListId?: string;
  index?: number;
  serialNumber?: string;
  fitment?: string;
};

export const useSelectItemEvent = () => {
  const buildEvent = useCallback(
    ({
      item,
      priceData,
      partName,
      locationId,
      itemSummary,
      isSearchPageEvent = false,
      itemListId,
      itemListName,
      index = 1,
      serialNumber,
      fitment
    }: AddToCartType) => {
      const cartDetails = {
        cartId: item.orderItemId
      };
      const categoryArr = itemSummary?.categoryPath?.split('/') || [];
      const { availabilityMessage } = !isSearchPageEvent
        ? getAttributeValueForCart([priceData], item.orderItemId)
        : (item as SearchSelectItemData);
      const {
        discountAmount = 0,
        totalPrice = 0,
        weight
      } = item as SearchSelectItemData;
      const productPrice = !isSearchPageEvent
        ? priceData?.priceWithGST.unformatted.totalPrice
        : totalPrice || 0;
      const addItems: CartItem = {
        item_id: item?.partNumber,
        item_name: partName,
        affiliation: undefined,
        coupon: undefined,
        discount: !isSearchPageEvent
          ? priceData?.priceWithGST?.unformatted?.discountAmount
          : discountAmount,
        index: index,
        item_brand: itemSummary?.manufacturer || undefined,
        item_category: categoryArr[0] || undefined,
        item_category2: categoryArr[1] || undefined,
        item_category3: categoryArr[2] || undefined,
        item_category4: categoryArr[3] || undefined,
        item_category5: categoryArr[4] || undefined,
        item_variant: undefined,
        location_id: locationId || undefined,
        price: productPrice,
        quantity: item.quantity ?? 1,
        p_inventory_status: availabilityMessage,
        p_weight: weight,
        p_serial_number: serialNumber || undefined,
        p_kit_id: undefined, // BE to send data in get cart deatils story is open
        p_check_fitment: serialNumber ? fitment : undefined,
        p_attributes_visible: undefined
      };

      return {
        cartDetails,
        ecommerce: {
          item_list_id: itemListId,
          item_list_name: itemListName,
          items: [addItems]
        },
        event: EVENT_SELECT_ITEM
      };
    },
    []
  );

  const fireEvent = useCallback(
    (data: AddToCartType) => {
      sendGTMEvent({ ecommerce: null });
      sendGTMEvent(buildEvent(data));
    },
    [buildEvent]
  );

  return { fireEvent };
};
